import { Home, Bath, Paintbrush, Wrench, Square } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { useTranslation } from "react-i18next";
import { useInView } from "../hooks/useInView";

const iconMap = {
  Home: Home,
  Bath: Bath,
  Paintbrush: Paintbrush,
  Wrench: Wrench,
  Square: Square
};

const Services = () => {
  const { t } = useTranslation();
  const [ref, inView] = useInView(0.1);

  const servicesData = [
    {
      id: 1,
      titleKey: "service.houseReno.title",
      descKey: "service.houseReno.desc",
      icon: "Home",
      image: "https://customer-assets.emergentagent.com/job_craft-house/artifacts/m8cv1u8h_VID-20250214-WA0015.mp4",
      mediaType: "video",
      features: ["feature1", "feature2", "feature3", "feature4"].map(f => `service.houseReno.${f}`)
    },
    {
      id: 2,
      titleKey: "service.bathroom.title",
      descKey: "service.bathroom.desc",
      icon: "Bath",
      image: "https://customer-assets.emergentagent.com/job_craft-house/artifacts/kshri1m8_VID-20240424-WA0019.mp4",
      mediaType: "video",
      features: ["feature1", "feature2", "feature3", "feature4"].map(f => `service.bathroom.${f}`)
    },
    {
      id: 3,
      titleKey: "service.painting.title",
      descKey: "service.painting.desc",
      icon: "Paintbrush",
      image: "https://customer-assets.emergentagent.com/job_craft-house/artifacts/j33amx9s_IMG_20210821_190504_473.webp",
      features: ["feature1", "feature2", "feature3", "feature4"].map(f => `service.painting.${f}`)
    },
    {
      id: 4,
      titleKey: "service.plumbing.title",
      descKey: "service.plumbing.desc",
      icon: "Wrench",
      image: "https://customer-assets.emergentagent.com/job_craft-house/artifacts/34oxtvnz_IMG_20200220_222422_998.jpg",
      features: ["feature1", "feature2", "feature3", "feature4"].map(f => `service.plumbing.${f}`)
    },
    {
      id: 5,
      titleKey: "service.plastering.title",
      descKey: "service.plastering.desc",
      icon: "Square",
      image: "https://customer-assets.emergentagent.com/job_craft-house/artifacts/51toa88w_IMG_20200604_220234_267.jpg",
      features: ["feature1", "feature2", "feature3", "feature4"].map(f => `service.plastering.${f}`)
    }
  ];

  return (
    <section id="services" ref={ref} className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <h2 className={`anim-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4 ${inView ? 'is-in' : ''}`}>
            {t('services.title')}
          </h2>
          <div className="flex justify-center mb-4">
            <div className={`anim-heading-underline ${inView ? 'is-in' : ''}`} />
          </div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('services.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {servicesData.map((service, idx) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card
                key={service.id}
                className={`anim-card anim-fade-up group hover:shadow-2xl border-0 overflow-hidden ${inView ? 'is-in' : ''}`}
                style={{ animationDelay: `${idx * 0.12}s` }}
              >
                <div className="relative h-48 overflow-hidden">
                  {service.mediaType === "video" ? (
                    <video
                      data-testid={`service-video-${service.id}`}
                      src={service.image}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  ) : (
                    <img
                      src={service.image}
                      alt={t(service.titleKey)}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <div className="anim-icon-circle bg-[#1a7a8a] p-3 rounded-lg">
                      {IconComponent && <IconComponent className="text-white" size={24} />}
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#1a7a8a] transition-colors">
                    {t(service.titleKey)}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {t(service.descKey)}
                  </p>
                  <ul className="space-y-2">
                    {service.features.map((featureKey) => (
                      <li key={featureKey} className="flex items-center text-sm text-gray-700">
                        <div className="w-1.5 h-1.5 bg-[#1a7a8a] rounded-full mr-2"></div>
                        {t(featureKey)}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
